import { useEffect, useState } from 'react';
import { Icon, Link, Text, Spinner } from '@nimbus-ds/components';
import { ClockIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import usePaymentMethods from 'domains/Billing/Checkout/hooks/usePaymentMethods';
import { useChargeCreation } from 'domains/Billing/Checkout/pages/PlansAndSubscriptions/components/PaymentsTabs/components/SelectChargesToPayTab/hooks';
import {
  trackingBillingAdbarUpsellDiscountClick,
  trackingBillingAdbarDiscountUpgradeShow,
} from 'domains/Billing/tracking';
import { AdBar } from './AdBar';

interface BillingUpsellDiscountProps {
  expirationDate: Date;
}

export function BillingUpsellDiscount({
  expirationDate,
}: BillingUpsellDiscountProps) {
  const { t } = useTranslation('common');
  const { onContinue, isLoading } = useChargeCreation();
  const { firstRecurrencyOptionAsCharge } = usePaymentMethods();
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const expirationInLocal = new Date(
      expirationDate.toLocaleString('en-US', { timeZone: userTimezone }),
    );
    const nowInLocal = new Date(
      new Date().toLocaleString('en-US', { timeZone: userTimezone }),
    );
    const diffInMs = expirationInLocal.getTime() - nowInLocal.getTime();
    return Math.max(0, Math.floor(diffInMs / 1000));
  });

  useEffect(() => {
    trackingBillingAdbarDiscountUpgradeShow();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const handleClick = async () => {
    trackingBillingAdbarUpsellDiscountClick();
    if (firstRecurrencyOptionAsCharge && !isLoading) {
      await onContinue([firstRecurrencyOptionAsCharge]);
    }
  };

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const seconds = secondsLeft % 60;
  const formatTime = (time: number) => time.toString().padStart(2, '0');
  const time = `${formatTime(hours)}:${formatTime(minutes)}:${formatTime(
    seconds,
  )}`;

  return (
    <AdBar appearence="warning">
      <Icon color="warning-textLow" source={<ClockIcon />} />
      <Text color="warning-textLow" fontWeight="bold">
        {t('topAdBar.billingUpsellDiscount.text', { time })}
      </Text>
      <Link
        as="button"
        onClick={handleClick}
        appearance="primary"
        textDecoration="none"
        disabled={isLoading}
      >
        {isLoading && <Spinner color="currentColor" size="small" />}
        {t('topAdBar.billingUpsellDiscount.link')}
      </Link>
    </AdBar>
  );
}
